import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/ui/Button'
import ThemeToggle from '../components/ui/ThemeToggle'
import SupportEmailCTA from '../components/ui/SupportEmailCTA'
import ZennaStatsBar from '../components/ZennaStatsBar'

const API_URL = import.meta.env.VITE_API_URL || ''

const Landing = () => {
  const navigate = useNavigate()
  const { login, loading, isAuthenticated, error: authError } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [siteStats, setSiteStats] = useState({ total_projects: 0, total_generations: 0 })

  // Capture ?ref= code from URL and stash for redemption after signup
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const ref = params.get('ref')
      if (ref && /^[A-Fa-f0-9]{8}$/.test(ref)) {
        localStorage.setItem('zenna_referral_code', ref.toUpperCase())
      }
    } catch (e) { /* ignore */ }
  }, [])

  // Fetch real stats from database
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/stats`)
        if (response.ok) {
          const data = await response.json()
          setSiteStats(data)
        }
      } catch (error) {
        console.error('Error fetching stats:', error)
      }
    }
    fetchStats()
  }, [])

  // If already logged in, redirect to dashboard
  if (isAuthenticated) {
    navigate('/dashboard')
    return null
  }

  const handleGetStarted = async () => {
    setMobileMenuOpen(false)
    await login()
  }

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-zenna-cream via-white to-zenna-sage/10 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900" />
      
      {/* Decorative blobs - hidden on mobile for performance */}
      <div className="hidden sm:block absolute top-20 left-10 w-72 h-72 bg-zenna-violet/10 rounded-full blur-3xl" />
      <div className="hidden sm:block absolute bottom-20 right-10 w-96 h-96 bg-zenna-cyan/10 rounded-full blur-3xl" />
      <div className="hidden sm:block absolute top-1/2 left-1/2 w-64 h-64 bg-zenna-emerald/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* Global Stats Bar */}
        <ZennaStatsBar />

        {/* Header */}
        <header className="flex items-center justify-between px-4 sm:px-6 py-4 lg:px-12">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-zenna flex items-center justify-center shadow-lg shadow-zenna-violet/20">
              <span className="text-white font-bold text-lg sm:text-xl">Z</span>
            </div>
            <span className="text-xl sm:text-2xl font-display font-bold text-slate-900 dark:text-white">
              Zenna
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <ThemeToggle />
            <Button variant="ghost" onClick={() => navigate('/marketplace')}>Marketplace</Button>
            <Button variant="ghost" onClick={() => navigate('/gallery')}>Gallery</Button>
            <Button variant="ghost" onClick={() => navigate('/pricing')}>Pricing</Button>
            <Button variant="secondary" onClick={handleGetStarted} loading={loading}>Sign In</Button>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <ThemeToggle />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6 text-slate-700 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-slate-700 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </header>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-24 left-0 right-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shadow-lg z-50">
            <div className="flex flex-col p-4 gap-2">
              <button onClick={() => { navigate('/marketplace'); setMobileMenuOpen(false) }} className="w-full text-left px-4 py-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium transition-colors">Marketplace</button>
              <button onClick={() => { navigate('/gallery'); setMobileMenuOpen(false) }} className="w-full text-left px-4 py-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium transition-colors">Gallery</button>
              <button onClick={() => { navigate('/pricing'); setMobileMenuOpen(false) }} className="w-full text-left px-4 py-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium transition-colors">Pricing</button>
              <Button className="w-full mt-2" onClick={handleGetStarted} loading={loading}>Sign In</Button>
            </div>
          </div>
        )}

        {/* Hero Section */}
        <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-20 sm:pb-32 lg:pt-32 flex-1">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-zenna-violet/10 dark:bg-zenna-violet/20 text-zenna-violet dark:text-zenna-violet mb-6 sm:mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-zenna-violet opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-zenna-violet"></span>
              </span>
              <span className="text-xs sm:text-sm font-medium">Your ultimate craft companion</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-display font-bold text-slate-900 dark:text-white mb-4 sm:mb-6 leading-tight">
              Your calm in the
              <br />
              <span className="text-gradient">crafting chaos</span>
            </h1>

            <p className="text-base sm:text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto mb-3 sm:mb-4 px-2">
              Chat with Zenna to create designs, add perfect text, and export 
              machine-ready files for Cricut and embroidery.
            </p>
            
            <p className="text-sm sm:text-lg text-zenna-violet dark:text-zenna-cyan font-medium mb-8 sm:mb-10">
              Bringing out the creator in everyone, one project at a time.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
              <Button size="lg" onClick={handleGetStarted} loading={loading} className="w-full sm:w-auto">Get Started Free</Button>
              <Button variant="secondary" size="lg" onClick={() => navigate('/pricing')} className="w-full sm:w-auto">View Pricing</Button>
            </div>

            {authError && (
              <div className="mt-4 mx-4 sm:mx-auto max-w-md p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300 text-center">
                {authError}
              </div>
            )}

            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-6 sm:mt-8">
              ✨ <span className="font-semibold">{siteStats.total_projects.toLocaleString()} projects</span> created
              {siteStats.total_generations > 0 && (<> • <span className="font-semibold">{siteStats.total_generations.toLocaleString()} designs</span> generated</>)}
              {' '}by makers like you
            </p>
          </div>

          {/* Pain Points Section */}
          <section className="mt-20 sm:mt-32">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-center text-slate-900 dark:text-white mb-3 sm:mb-4">Sound familiar?</h2>
            <p className="text-center text-slate-500 dark:text-slate-400 mb-8 sm:mb-12 max-w-2xl mx-auto text-sm sm:text-base">We built Zenna because we were frustrated too.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {[
                { problem: 'Hours hunting patterns', solution: "Describe what you want to make — Zenna creates it in seconds. Any craft, any style.", icon: '✨' },
                { problem: 'Generic one-size patterns', solution: "Custom-fit sewing patterns from your measurements or even a photo. 65+ garment types.", icon: '✂️' },
                { problem: '5+ apps to make one design', solution: 'All-in-one: generate → edit → export. One place, one flow.', icon: '🎯' },
                { problem: 'No idea where to start', solution: 'Zenna walks you through materials, steps, and techniques for 60+ craft types.', icon: '💜' }
              ].map((item, i) => (
                <div key={i} className="p-5 sm:p-6 rounded-2xl bg-white/50 dark:bg-slate-800/50 backdrop-blur border border-slate-200 dark:border-slate-700 hover:border-zenna-violet/50 transition-all duration-300">
                  <span className="text-3xl sm:text-4xl block mb-3 sm:mb-4">{item.icon}</span>
                  <h3 className="font-bold text-base sm:text-lg text-slate-900 dark:text-white mb-2">{item.problem}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm">{item.solution}</p>
                </div>
              ))}
            </div>
          </section>

          {/* How It Works */}
          <section className="mt-20 sm:mt-32">
            <h2 className="text-2xl sm:text-3xl font-display font-bold text-center text-slate-900 dark:text-white mb-3 sm:mb-4">How it works</h2>
            <p className="text-center text-slate-500 dark:text-slate-400 mb-8 sm:mb-12 text-sm sm:text-base">Three simple steps to machine-ready designs.</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-8">
              {[
                { step: '01', title: 'Chat with Zenna', description: 'Describe what you want. Zenna guides you through options, styles, and refinements.', icon: '💬' },
                { step: '02', title: 'Perfect your design', description: "Add text with 50,000+ fonts. Adjust colors, layouts, and details until it's just right.", icon: '✨' },
                { step: '03', title: 'Export & create', description: 'Download clean SVG for Cricut or optimized PES/DST for embroidery. Ready to make.', icon: '📥' }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-zenna text-white text-xl sm:text-2xl mb-3 sm:mb-4 shadow-lg shadow-zenna-violet/20">{item.icon}</div>
                  <div className="text-xs font-mono text-zenna-violet dark:text-zenna-cyan mb-1 sm:mb-2">STEP {item.step}</div>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base">{item.description}</p>
                </div>
              ))}
            </div>
          </section>
        </main>

        {/* Support Email CTA */}
        <SupportEmailCTA />

        {/* Footer */}
        <footer className="border-t border-slate-200 dark:border-slate-800 py-6 sm:py-8 px-4 sm:px-6">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-zenna flex items-center justify-center">
                <span className="text-white font-bold text-xs sm:text-sm">Z</span>
              </div>
              <span className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm">
                © 2026 Zenna. A ZenFlow Studios creation.
              </span>
            </div>
            <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              <a href="#" className="hover:text-zenna-violet transition-colors">Terms</a>
              <a href="#" className="hover:text-zenna-violet transition-colors">Privacy</a>
              <a href="#" className="hover:text-zenna-violet transition-colors">Contact</a>
            </div>
          </div>
        </footer>
        
        {/* Safety Warning Banner - moved to bottom */}
        <div className="bg-red-600 text-white text-center py-2 px-4 text-xs sm:text-sm">
          ⚠️ Zenna is designed for craft projects only. Artistic nudity for legitimate purposes is permitted, but suspicious requests for explicit or illegal content are logged and may result in account suspension and/or be reported to authorities.
        </div>
      </div>
    </div>
  )
}

export default Landing